import { FormattedMessage } from 'react-intl';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { breakpoint } from '@zocdoc/components/src/grid';
import { navy, white, grey10 } from '@zocdoc/branding/colors';
import { FontFamily } from '@zocdoc/components/src/typography';
import { EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';

import allergistImg from './assets/pollen.svg';
import { ProcedureIds, SpecialtyIds } from '../../../constants';
import { LOCALIZED_STRINGS } from '../../constants';
import { CLICK_TRACKING_EVENT_DESCRIPTIONS } from './constants';
import physicalImg from './assets/physical.svg';
import rightArrow from './assets/rightArrow.svg';
import therapistImg from './assets/therapist.svg';
import fluShotImg from './assets/fluShot.svg';
import { VARIANT_OFF, isVariantAorB } from '../../../config/ab/experiments';

class ThemedSearchPanel extends PureComponent {
    static propTypes = {
        pendingSearchParameters: PropTypes.object,
        sendMetric: PropTypes.func,
        updateParameters: PropTypes.func,
        subwayHomePageVariation: PropTypes.string,
    };

    static defaultProps = {
        pendingSearchParameters: {},
        sendMetric: noop,
        updateParameters: noop,
        subwayHomePageVariation: VARIANT_OFF,
    };

    render() {
        return (
            <Wrapper
                data-test="themed-search"
                subwayHomePageVariation={this.props.subwayHomePageVariation}
            >
                <FluShotTile
                    data-test="flu-shot-tile"
                    onClick={this.handleClickFluShot}
                >
                    <Tagline><FormattedMessage {...LOCALIZED_STRINGS.THEMED_SEARCH_FLU_SHOT_TAGLINE} /></Tagline>
                    <Spreader />
                    <CallToAction>
                        <FormattedMessage {...LOCALIZED_STRINGS.THEMED_SEARCH_FLU_SHOT_CTA} />
                        <RightArrow />
                    </CallToAction>
                </FluShotTile>

                <PhysicalTile
                    data-test="physical-tile"
                    onClick={this.handleClickPhysical}
                >
                    <Tagline><FormattedMessage {...LOCALIZED_STRINGS.THEMED_SEARCH_PHYSICAL_TAGLINE} /></Tagline>
                    <Spreader />
                    <CallToAction>
                        <FormattedMessage {...LOCALIZED_STRINGS.THEMED_SEARCH_PHYSICAL_CTA} />
                        <RightArrow />
                    </CallToAction>
                </PhysicalTile>

                <AllergistTile
                    data-test="allergist-tile"
                    onClick={this.handleClickAllergist}
                >
                    <Tagline><FormattedMessage {...LOCALIZED_STRINGS.THEMED_SEARCH_ALLERGIST_TAGLINE} /></Tagline>
                    <Spreader />
                    <CallToAction>
                        <FormattedMessage {...LOCALIZED_STRINGS.THEMED_SEARCH_ALLERGIST_CTA} />
                        <RightArrow />
                    </CallToAction>
                </AllergistTile>

                <TherapistTile
                    data-test="therapist-tile"
                    onClick={this.handleClickTherapist}
                >
                    <Tagline><FormattedMessage {...LOCALIZED_STRINGS.THEMED_SEARCH_THERAPIST_TAGLINE} /></Tagline>
                    <Spreader />
                    <CallToAction>
                        <FormattedMessage {...LOCALIZED_STRINGS.THEMED_SEARCH_THERAPIST_CTA} />
                        <RightArrow />
                    </CallToAction>
                </TherapistTile>

            </Wrapper>
        );
    }

    handleClickAllergist = () => {
        this.props.sendMetric({
            action: CLICK_TRACKING_EVENT_DESCRIPTIONS.ALLERGIST,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        this.executeSearch(ProcedureIds.ALLERGY_CONSULTATION, SpecialtyIds.ALLERGIST);
    };

    handleClickPhysical = () => {
        this.props.sendMetric({
            action: CLICK_TRACKING_EVENT_DESCRIPTIONS.PHYSICAL,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        this.executeSearch(ProcedureIds.ANNUAL_PHYSICAL, SpecialtyIds.PRIMARY_CARE_PHYSICIAN);
    };

    handleClickTherapist = () => {
        this.props.sendMetric({
            action: CLICK_TRACKING_EVENT_DESCRIPTIONS.THERAPIST,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        this.executeSearch(ProcedureIds.PSYCHIATRY_CONSULTATION, SpecialtyIds.PSYCHIATRIST);
    };

    handleClickFluShot = () => {
        this.props.sendMetric({
            action: CLICK_TRACKING_EVENT_DESCRIPTIONS.FLU_SHOT,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        this.executeSearch(ProcedureIds.FLU_SHOT, SpecialtyIds.PRIMARY_CARE_PHYSICIAN);
    };

    executeSearch = (procedureId, specialtyId) => {
        const updatedPendingSearchParameters = Object.assign(this.props.pendingSearchParameters, {
            specialtyId,
            procedureId,
        });

        this.props.updateParameters(updatedPendingSearchParameters);
    }
}

const Tile = styled.div`
    background-color: ${white};
    background-repeat: no-repeat;
    border: 4px solid ${grey10};
    box-sizing: border-box;
    color: ${navy};
    cursor: pointer;
    display: flex;
    flex-basis: 100%;
    flex-direction: column;
    height: 180px;

    ${breakpoint.small} {
        flex-basis: 50%;
    }

    ${breakpoint.large} {
        flex-basis: 25%;
    }
`;

const AllergistTile = Tile.extend`
    background-image: url(${allergistImg});
    background-size: auto 60%;
    background-position: center bottom;
    object-fit: contain;
`;

const CallToAction = styled.div`
    align-self: flex-end;
    display: flex;
    font: 16px/1 ${FontFamily.SharpSansSemibold};
    padding-bottom: 20px;
`;

const PhysicalTile = Tile.extend`
    background-image: url(${physicalImg});
    background-position: 90% 100%;
    background-size: auto 70%;
    object-fit: contain;
`;

const FluShotTile = Tile.extend`
    background-image: url(${fluShotImg});
    background-size: auto 54%;
    background-position: bottom left 30%;
    object-fit: contain;
`;

const RightArrow = styled.div`
    background-image: url(${rightArrow});
    background-size: auto 80%;
    background-repeat: no-repeat;
    background-position: bottom 1px left;
    margin-left: 8px;
    object-fit: contain;
    width: 50px;
`;

const Spreader = styled.div`
    flex: 1;
`;

const Tagline = styled.div`
    font: 20px/24px ${FontFamily.SharpSansBold};
    padding: 20px 30px 0 30px;
`;

const TherapistTile = Tile.extend`
    background-image: url(${therapistImg});
    background-position: bottom 50% right 20%;
    background-size: auto 35%;
    object-fit: contain;
`;

const Wrapper = styled.div`
    box-sizing: border-box;
    border: 4px solid ${grey10};
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    padding-top: ${props => isVariantAorB(props.subwayHomePageVariation) ? '0px;' : '100px;'}
    padding-bottom: ${props => isVariantAorB(props.subwayHomePageVariation) ? '50px;' : '0px;'}
`;

export default ThemedSearchPanel;
